import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Checkbox, Col, Form, Row, Table, Typography } from "antd";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { EUserRole, ServiceTemplateField, TemplateMetaDataField } from "@devowl-wp/api-real-cookie-banner";
import { useServiceTemplatePurposeTableColumns } from "./purposes/columns.js";
import { PurposeTable } from "./purposes/purpose-table.js";
import { useServiceTemplatePurposeOptions, useTranslatePurposeKey } from "./purposes/useQuestions.js";
import { useTranslation } from "../../../../../../others/i18n.js";
import { useStores } from "../../../../../../store/root.js";
import { isExtendedFieldHidden } from "../../../../../../util/template/form.js";
import { getDisableStrategies } from "../../../../../../util/template/merge-strategies.js";
import { getFieldNameTranslated } from "../../../../../../util/template/translation.js";
import { FormFieldVisibilityWrapper, FormFieldVisibilityWrapperByCdn } from "../../formFieldVisibilityWrapper.js";
import { TemplateMetaDataExtendsStrategyFormField } from "../../templateMetaDataExtendsStrategyFormField.js";
/**
 * Form field for the purposes of a service template.
 * It contains a catalog of purposes that can be configured by the user.
 * As a result, the purpose text is composed from the answers to the questions.
 *
 * @returns The form field.
 */ const FormFieldServiceTemplatePurposes = observer(()=>{
    const { t } = useTranslation();
    const { rcbAuthenticationStore } = useStores();
    const hasCreatorRole = rcbAuthenticationStore.hasRole(EUserRole.Creator);
    // DND table for purposes
    const purposeOptions = useMemo(()=>{
        return useServiceTemplatePurposeOptions(t);
    }, []);
    const purposeColumns = useServiceTemplatePurposeTableColumns(t, purposeOptions);
    const { Text } = Typography;
    return /*#__PURE__*/ _jsx(FormFieldVisibilityWrapperByCdn, {
        children: /*#__PURE__*/ _jsx(FormFieldVisibilityWrapper, {
            fieldNames: ServiceTemplateField.Purposes,
            forceHiddenFormCallback: isExtendedFieldHidden(ServiceTemplateField.Purposes, hasCreatorRole),
            children: /*#__PURE__*/ _jsx(Form.Item, {
                label: getFieldNameTranslated(ServiceTemplateField.Purposes, t),
                extra: t("The purpose is composed as a continuous text from the answers. Questions are not displayed in the cookie banner. Custom questions can be added."),
                shouldUpdate: (prev, next)=>prev.extends?.identifier !== next.extends?.identifier,
                required: true,
                children: ({ getFieldValue })=>{
                    const extended = getFieldValue(TemplateMetaDataField.Extends);
                    const extendedPurposes = extended?.purposes;
                    return /*#__PURE__*/ _jsx(TemplateMetaDataExtendsStrategyFormField, {
                        extendsFieldName: ServiceTemplateField.Purposes,
                        disableStrategies: getDisableStrategies(true),
                        onKeepContent: extended && /*#__PURE__*/ _jsx(Table, {
                            rowKey: "id",
                            pagination: false,
                            columns: [
                                {
                                    title: t("#"),
                                    dataIndex: "orderPosition",
                                    render: (_)=>_ + 1,
                                    align: "center"
                                },
                                {
                                    title: t("Question"),
                                    dataIndex: "question",
                                    render: (_, record)=>/*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsx(Row, {
                                                    children: /*#__PURE__*/ _jsx(Col, {
                                                        children: /*#__PURE__*/ _jsx(Text, {
                                                            strong: true,
                                                            children: useTranslatePurposeKey(t, record.question, record.question)
                                                        })
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsx(Row, {
                                                    children: /*#__PURE__*/ _jsx(Col, {
                                                        children: /*#__PURE__*/ _jsx(Text, {
                                                            children: record.answer
                                                        })
                                                    })
                                                })
                                            ]
                                        })
                                },
                                {
                                    title: t("Paragraph"),
                                    dataIndex: "isNewParagraph",
                                    render: (_)=>/*#__PURE__*/ _jsx(Checkbox, {
                                            checked: _,
                                            disabled: true
                                        })
                                }
                            ],
                            dataSource: extendedPurposes
                        }),
                        children: /*#__PURE__*/ _jsx(PurposeTable, {
                            formName: ServiceTemplateField.Purposes,
                            columns: purposeColumns,
                            purposeOptions: purposeOptions
                        })
                    });
                }
            })
        })
    });
});
export { FormFieldServiceTemplatePurposes };
